/**
 * PujcovnaPrehledPage — today's board of the rental (`/pujcovna/prehled`): the cars that are late, the ones waiting for their handover,
 * what is due back and out today and tomorrow. The server classifies (`GET /v1/pujcovna/prehled`, judged at Prague's now, refreshed every
 * minute); at `pujcovna:write` a row hands the car over or takes it back through the same dialogs as the reservation list. Clicking a row
 * opens the reservation form (change / shorten / prolong; read-only below `write`), the same one the Rezervace tab opens.
 */

import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Badge } from '@/components/Badge';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Marker } from '@/components/Marker';
import { Pill } from '@/components/Pill';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { useModuleAccess } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useFleetQuery, usePobockyQuery, usePrehledQuery, type Rezervace } from './api';
import { PredaniModal } from './PredaniModal';
import { PujcovnaSubtabs } from './PujcovnaSubtabs';
import { RezervaceModal } from './RezervaceModal';
import { useRezervaceVUrl } from './rezervace-url';
import { VozidloOdkaz } from './VozidloOdkaz';
import { VraceniModal } from './VraceniModal';
import { minutMezi, sekcePrehledu, type PrehledSekce } from './prehled-helpers';
import { duvodJenCteni, formatDateTime, lzePredatZFormulare, lzePrijmoutZFormulare } from './rezervace-helpers';
import { zpozdeniText } from './vraceni-helpers';

export function PujcovnaPrehledPage() {
  const { can, resolved } = useModuleAccess();
  const canRead = resolved && can('pujcovna', 'read');
  const canWrite = resolved && can('pujcovna', 'write');
  const canAdmin = resolved && can('pujcovna', 'admin');
  const { isPhone } = useBreakpoint();
  const prehled = usePrehledQuery(canRead);
  const fleet = useFleetQuery(canRead);
  const pobocky = usePobockyQuery();
  const carById = useMemo(() => new Map((fleet.data ?? []).map((f) => [f.id, f])), [fleet.data]);
  const [editing, setEditing] = useState<Rezervace | null>(null);
  const [predavani, setPredavani] = useState<Rezervace | null>(null);
  const [prijimani, setPrijimani] = useState<Rezervace | null>(null);
  // the open form is in the address, so a printout opened from it can come back to it
  useRezervaceVUrl('/pujcovna/prehled', editing?.id ?? null, setEditing);

  const nyni = prehled.data?.nyni ?? '';
  const sekce = useMemo(() => (prehled.data ? sekcePrehledu(prehled.data) : []), [prehled.data]);
  const neprazdne = sekce.filter((s) => s.rows.length > 0);

  // the board refetches every minute: the shortcuts out of the open form act on the row as it is now, not as it was clicked
  const cerstva = (r: Rezervace) => sekce.flatMap((s) => s.rows).find((x) => x.id === r.id) ?? r;
  const aktualni = editing ? cerstva(editing) : null;

  const akce = (s: PrehledSekce, r: Rezervace) =>
    canWrite && carById.has(r.id_vozidla) ? (
      s.akce === 'predat' ? (
        <Button variant="secondary" size="sm" data-testid={`prehled-predat-${r.id}`} onClick={() => setPredavani(r)}>
          Předat vůz
        </Button>
      ) : (
        <Button variant="secondary" size="sm" data-testid={`prehled-prijmout-${r.id}`} onClick={() => setPrijimani(r)}>
          Přijmout vozidlo
        </Button>
      )
    ) : null;

  const columns = (s: PrehledSekce): DataTableColumn<Rezervace>[] => [
    {
      id: 'termin',
      header: 'Termín',
      width: 'minmax(220px, 1.2fr)',
      priority: 'primary',
      cell: (r) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-mono text-[12.5px] text-ink">
            {formatDateTime(r.od)} – {formatDateTime(r.do)}
          </span>
          {s.id === 'po_terminu' && (
            <span data-testid={`prehled-zpozdeni-${r.id}`} className="text-[12px] text-red-text">
              Zpoždění {zpozdeniText(minutMezi(r.do, nyni))}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'vozidlo',
      header: 'Vozidlo',
      width: 'minmax(170px, 1fr)',
      priority: 'secondary',
      cell: (r) => {
        const car = carById.get(r.id_vozidla);
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-[13px] text-ink">
              <VozidloOdkaz entry={car}>
                {car?.vozidlo ? [car.vozidlo.znacka_nazev, car.vozidlo.model_nazev].filter(Boolean).join(' ') || 'Vozidlo' : 'Vozidlo'}
              </VozidloOdkaz>
            </span>
            {car?.vozidlo?.spz && (
              <span>
                <VozidloOdkaz entry={car}>
                  <Pill>{car.vozidlo.spz}</Pill>
                </VozidloOdkaz>
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'zakaznik',
      header: 'Zákazník',
      width: 'minmax(160px, 1fr)',
      priority: 'secondary',
      cell: (r) =>
        r.typ === 'technicky' || r.typ === 'servis' ? (
          <Badge tone={r.typ === 'servis' ? 'danger' : 'neutral'} size="sm">
            {r.typ === 'servis' ? 'Servis' : 'Technická rezervace'}
          </Badge>
        ) : (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13px] text-ink">
              {r.id_zakaznika != null ? (
                <Link to="/zakaznici/$id" params={{ id: String(r.id_zakaznika) }} className="underline-offset-2 hover:underline">
                  {r.zakaznik_nazev ?? '—'}
                </Link>
              ) : (
                (r.zakaznik_nazev ?? '—')
              )}
            </span>
            {r.zakaznik_telefon && <span className="font-mono text-[11px] text-ink-3">{r.zakaznik_telefon}</span>}
          </span>
        ),
    },
    {
      id: 'cislo',
      header: 'ID',
      width: '64px',
      priority: 'meta',
      cell: (r) => <span className="font-mono text-[12px] text-ink-2">{r.id}</span>,
    },
    ...(isPhone
      ? []
      : [
          {
            id: 'akce',
            header: '',
            width: '160px',
            align: 'right',
            priority: 'meta',
            cell: (r: Rezervace) => akce(s, r),
          } satisfies DataTableColumn<Rezervace>,
        ]),
  ];

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Půjčovna', href: '/pujcovna' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>
            Přehled,
            <br />
            <em>co je dnes venku.</em>
          </DisplayHeading>
          {prehled.data && (
            <CrownMeta
              items={[
                { label: 'Venku', value: prehled.data.venku },
                { label: 'Po termínu', value: prehled.data.po_terminu.length },
              ]}
            />
          )}
        </Crown.Row>
        <PujcovnaSubtabs active="prehled" />
      </Crown>

      <div data-testid="prehled-page" className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-8 pb-8 pt-5">
        {!resolved ? (
          <Skeleton className="h-10 w-full" />
        ) : !canRead ? (
          <EmptyState eyebrow="403" title="Přehled vyžaduje přístup k modulu Půjčovna." description="Pro přístup požádejte správce firmy." />
        ) : prehled.isError || fleet.isError ? (
          <EmptyState eyebrow="Chyba" title="Přehled se nepodařilo načíst" description="Zkuste to prosím znovu za chvíli." />
        ) : prehled.isPending || fleet.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : neprazdne.length === 0 ? (
          <EmptyState eyebrow="Klid" title="Dnes a v příštích 3 dnech nic nečeká" description="Žádné vozidlo není venku ani po termínu a nic se nepředává ani nevrací." />
        ) : (
          neprazdne.map((s) => (
            <section key={s.id} data-testid={`prehled-sekce-${s.id}`} className="flex flex-col gap-2">
              <Marker tone={s.tone}>
                {s.title} · {s.rows.length}
              </Marker>
              <DataTable<Rezervace>
                aria-label={s.title}
                variant="bordered"
                columns={columns(s)}
                data={s.rows}
                getRowId={(r) => String(r.id)}
                onRowClick={setEditing}
                renderRowDetail={isPhone ? (r) => akce(s, r) : undefined}
              />
            </section>
          ))
        )}
      </div>

      {aktualni && (
        <RezervaceModal
          // the form is seeded once from its row: a new instance per reservation, and again when someone else changes it meanwhile
          key={`r${aktualni.id}|${aktualni.od}|${aktualni.do}|${aktualni.id_zakaznika}|${aktualni.poznamka}|${aktualni.predano_at}`}
          rezervace={aktualni}
          fleet={fleet.data ?? []}
          pobocky={pobocky.data ?? []}
          canOverride={canAdmin}
          jenCteni={duvodJenCteni(canWrite, carById.has(aktualni.id_vozidla))}
          onPredat={
            lzePredatZFormulare(aktualni, canWrite)
              ? () => {
                  setPredavani(aktualni);
                  setEditing(null);
                }
              : undefined
          }
          onPrijmout={
            lzePrijmoutZFormulare(aktualni, canWrite)
              ? () => {
                  setPrijimani(aktualni);
                  setEditing(null);
                }
              : undefined
          }
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
      {predavani && (
        <PredaniModal
          key={predavani.id}
          rezervace={predavani}
          title={carById.get(predavani.id_vozidla)?.vozidlo?.spz || predavani.vin}
          canOverride={canAdmin}
          open
          onOpenChange={(open) => !open && setPredavani(null)}
        />
      )}
      {prijimani && (
        <VraceniModal
          key={prijimani.id}
          rezervace={prijimani}
          title={carById.get(prijimani.id_vozidla)?.vozidlo?.spz || prijimani.vin}
          open
          onOpenChange={(open) => !open && setPrijimani(null)}
        />
      )}
    </Shell>
  );
}
